<template>
  <b-table
    responsive
    :items="items"
    :fields="fields"
    class="umdb-decklist compact-table-sm"
    :sticky-header="stickyHeader"
    no-border-collapse
    primary-key="slug"
  >
    <template v-slot:thead-top>
      <b-tr>
        <b-th><span class="sr-only">Deck Name</span></b-th>
        <b-th colspan="5" v-if="showGroup('info')">Hero(es)</b-th>
        <b-th colspan="4" v-if="showGroup('info')">Sidekick(s)</b-th>
        <b-th colspan="5" v-if="showGroup('stats')">Unique cards</b-th>
        <b-th colspan="5" v-if="showGroup('stats')">Total cards</b-th>
        <b-th colspan="3" v-if="showGroup('stats')">Total value</b-th>
        <b-th colspan="1" v-if="showGroup('stats')">Visualization</b-th>
      </b-tr>
    </template>

    <template v-slot:cell(deckName)="data">
      <router-link :to="{ name: 'UmDbDeck', params: { slug: data.item.slug } }">
        {{ data.value }}
      </router-link>
    </template>

    <template v-slot:cell(heroAttack)="data">
      <picture class="attack-type-icon" v-if="data.value === 'melee'">
        <source
          media="(max-width: 768px)"
          srcset="@/assets/images/melee-icon.svg"
        />
        <img src="@/assets/images/melee.svg" class="attack-type-icon" />
      </picture>
      <picture class="attack-type-icon" v-else-if="data.value === 'ranged'">
        <source
          media="(max-width: 768px)"
          srcset="@/assets/images/ranged-icon.svg"
        />
        <img src="@/assets/images/ranged.svg" class="attack-type-icon" />
      </picture>
      <span v-else>{{ data.value }}</span>
    </template>
    <template v-slot:cell(sidekickAttack)="data">
      <picture class="attack-type-icon" v-if="data.value === 'melee'">
        <source
          media="(max-width: 768px)"
          srcset="@/assets/images/melee-icon.svg"
        />
        <img src="@/assets/images/melee.svg" class="attack-type-icon" />
      </picture>
      <picture class="attack-type-icon" v-else-if="data.value === 'ranged'">
        <source
          media="(max-width: 768px)"
          srcset="@/assets/images/ranged-icon.svg"
        />
        <img src="@/assets/images/ranged.svg" class="attack-type-icon" />
      </picture>
      <span v-else>{{ data.value }}</span>
    </template>

    <template v-slot:cell(heroQuantity)="data">
      <span v-if="data.value > 1">&times;{{ data.value }}</span>
    </template>

    <template v-slot:cell(sidekickQuantity)="data">
      <span v-if="data.value > 1">&times;{{ data.value }}</span>
    </template>

    <!-- Unique cards -->
    <template v-slot:head(uniqueAttack)>
      <img src="@/assets/images/attack.svg" :style="{ width: '1.2em' }" />
    </template>

    <template v-slot:head(uniqueVersatile)>
      <img src="@/assets/images/versatile.svg" :style="{ width: '1.2em' }" />
    </template>

    <template v-slot:head(uniqueDefence)>
      <img src="@/assets/images/defence.svg" :style="{ width: '1.0em' }" />
    </template>

    <template v-slot:head(uniqueScheme)>
      <img src="@/assets/images/scheme.svg" :style="{ height: '1.5em' }" />
    </template>

    <!-- Total cards-->
    <template v-slot:head(countAttack)>
      <img src="@/assets/images/attack.svg" :style="{ width: '1.2em' }" />
    </template>

    <template v-slot:head(countVersatile)>
      <img src="@/assets/images/versatile.svg" :style="{ width: '1.2em' }" />
    </template>

    <template v-slot:head(countDefence)>
      <img src="@/assets/images/defence.svg" :style="{ width: '1.0em' }" />
    </template>

    <template v-slot:head(countScheme)>
      <img src="@/assets/images/scheme.svg" :style="{ height: '1.5em' }" />
    </template>

    <!-- Card values -->
    <template v-slot:head(valueAttack)>
      <img src="@/assets/images/attack.svg" :style="{ width: '1.2em' }" />
    </template>

    <template v-slot:head(valueVersatile)>
      <img src="@/assets/images/versatile.svg" :style="{ width: '1.2em' }" />
    </template>

    <template v-slot:head(valueDefence)>
      <img src="@/assets/images/defence.svg" :style="{ width: '1.0em' }" />
    </template>

    <!-- Histogram -->
    <template v-slot:cell(histogram)="data">
      <DeckHistogram :cards="data.item.cards" />
    </template>
  </b-table>
</template>

<script type="text/javascript">
import Stats from "@/deckstats";

import DeckHistogram from "@/components/DeckHistogram.vue";

export default {
  name: "UmDbDeckList",
  components: { DeckHistogram },
  props: {
    decks: {
      type: Array,
      required: true,
    },
    includeVersatile: {
      type: Boolean,
      default: false,
    },
    selectedGroups: {
      default: [],
    },
    stickyHeader: {
      default: false,
    },
  },
  computed: {
    items() {
      return this.decks.map((deck) => {
        // Todo handle multiple different heroes and sidekicks
        const hero = deck.heroes.length
          ? deck.heroes[0]
          : {
              attack_type: null,
              hp: null,
              name: null,
              quantity: 0,
            };
        const sidekick = deck.sidekicks.length
          ? deck.sidekicks[0]
          : {
              attack_type: null,
              hp: null,
              name: null,
              quantity: 0,
            };

        const deckStats = deck.cards.reduce(
          (stats, card) => {
            stats.all.update(card);

            const characterName = card.characterName.toLocaleUpperCase();

            if (characterName === "ANY") {
              stats.any.update(card);
            } else {
              if (!(characterName in stats.byCharacter))
                stats.byCharacter[characterName] = new Stats();

              const characterStats = stats.byCharacter[characterName];

              characterStats.update(card);
            }

            return stats;
          },
          {
            all: new Stats(),
            any: new Stats(),
            byCharacter: {},
          }
        );

        const deckEntry = {
          deckName: deck.name,
          slug: deck.slug,
          heroName: hero.name,
          heroAttack: hero.attack_type,
          heroHp: hero.hp,
          heroQuantity: hero.quantity,
          movement: deck.movement,
          sidekickName: sidekick.name,
          sidekickAttack: sidekick.attack_type,
          sidekickHp: sidekick.hp,
          sidekickQuantity: sidekick.quantity,
          deckStats: {
            ...deckStats.all,
            attack: this.includeVersatile
              ? deckStats.all.totalAttack
              : deckStats.all.attack,
            defence: this.includeVersatile
              ? deckStats.all.totalDefence
              : deckStats.all.defence,
          },
          cards: deck.cards,
        };

        return deckEntry;
      });
    },
    fields() {
      return [
        {
          key: "deckName",
          stickyColumn: true,
          isRowHeader: true,
          sortable: true,
          class: ["align-middle truncate-col"],
        },
        {
          key: "heroName",
          label: "Name",
          sortable: true,
          group: "info",
          tdClass: ["align-middle"],
        },
        {
          key: "heroQuantity",
          label: "#",
          tdClass: ["text-right align-middle"],
          sortable: true,
          group: "info",
        },
        {
          key: "heroAttack",
          label: "Attack",
          sortable: true,
          group: "info",
        },
        {
          key: "heroHp",
          label: "HP",
          sortable: true,
          group: "info",
          tdClass: ["text-right align-middle"],
        },
        {
          key: "movement",
          label: "Move",
          tdClass: ["text-right align-middle"],
          sortable: true,
          group: "info",
        },
        {
          key: "sidekickName",
          label: "Name",
          sortable: true,
          group: "info",
          tdClass: ["align-middle"],
        },
        {
          key: "sidekickQuantity",
          label: "#",
          tdClass: ["text-right align-middle"],
          sortable: true,
          group: "info",
        },
        {
          key: "sidekickAttack",
          label: "Attack",
          sortable: true,
          group: "info",
        },
        {
          key: "sidekickHp",
          label: "HP",
          sortable: true,
          group: "info",
          tdClass: ["text-right align-middle"],
        },
        {
          key: "uniqueAttack",
          class: [
            "attack align-middle text-center",
            this.includeVersatile ? " include-versatile" : "",
          ],
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.attack.unique,
          group: "stats",
        },
        {
          key: "uniqueVersatile",
          class: "versatile align-middle text-center",
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.versatile.unique,
          group: "stats",
        },
        {
          key: "uniqueDefence",
          class: [
            "defence align-middle text-center",
            this.includeVersatile ? " include-versatile" : "",
          ],
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.defence.unique,
          group: "stats",
        },
        {
          key: "uniqueScheme",
          class: "scheme align-middle text-center",
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.scheme.unique,
          group: "stats",
        },
        {
          key: "uniqueCard",
          label: "Total",
          sortable: true,
          sortByFormatted: true,
          tdClass: ["text-center align-middle"],
          formatter: (value, key, item) => item.deckStats.unique,
          group: "stats",
        },
        {
          key: "countAttack",
          class: [
            "attack align-middle text-center",
            this.includeVersatile ? " include-versatile" : "",
          ],
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.attack.count,
          group: "stats",
        },
        {
          key: "countVersatile",
          class: "versatile align-middle text-center",
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.versatile.count,
          group: "stats",
        },
        {
          key: "countDefence",
          class: [
            "defence align-middle text-center",
            this.includeVersatile ? " include-versatile" : "",
          ],
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.defence.count,
          group: "stats",
        },
        {
          key: "countScheme",
          class: "scheme align-middle text-center",
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.scheme.count,
          group: "stats",
        },
        {
          key: "countCard",
          label: "Total",
          sortable: true,
          sortByFormatted: true,
          tdClass: ["text-center align-middle"],
          formatter: (value, key, item) => item.deckStats.count,
          group: "stats",
        },
        {
          key: "valueAttack",
          class: [
            "attack align-middle text-center",
            this.includeVersatile ? " include-versatile" : "",
          ],
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.attack.value,
          group: "stats",
        },
        {
          key: "valueVersatile",
          class: "versatile align-middle text-center",
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.versatile.value,
          group: "stats",
        },
        {
          key: "valueDefence",
          class: [
            "defence align-middle text-center",
            this.includeVersatile ? " include-versatile" : "",
          ],
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => item.deckStats.defence.value,
          group: "stats",
        },
        {
          key: "histogram",
          label: "Value distribution",
          tdClass: "histogram",
          group: "stats",
        },
      ].filter(
        (value) => value.group === undefined || this.showGroup(value.group)
      );
    },
  },
  methods: {
    showGroup(group) {
      return (
        this.selectedGroups.length === 0 || this.selectedGroups.includes(group)
      );
    },
  },
};
</script>

<style lang="less">
@defence-blue: #2c76ac;
@attack-red: #dc3034;
@versatile-violet: #6c4e8d;
@scheme-yellow: #fcbd71;
.umdb-decklist {
  .attack-type-icon {
    width: 8em;
    @media (max-width: 768px) {
      width: 4em;
    }
  }
  .attack {
    background-color: @attack-red !important;
    color: white;
    &.include-versatile {
      text-shadow: @attack-red 5px 0px 0px, @attack-red 4.90033px 0.993347px 0px,
        @attack-red 4.60531px 1.94709px 0px, @attack-red 4.12668px 2.82321px 0px,
        @attack-red 3.48353px 3.58678px 0px, @attack-red 2.70151px 4.20736px 0px,
        @attack-red 1.81179px 4.6602px 0px, @attack-red 0.849836px 4.92725px 0px,
        @attack-red -0.145998px 4.99787px 0px,
        @attack-red -1.13601px 4.86924px 0px,
        @attack-red -2.08073px 4.54649px 0px,
        @attack-red -2.94251px 4.04248px 0px,
        @attack-red -3.68697px 3.37732px 0px,
        @attack-red -4.28444px 2.57751px 0px,
        @attack-red -4.71111px 1.67494px 0px,
        @attack-red -4.94996px 0.7056px 0px,
        @attack-red -4.99147px -0.291871px 0px,
        @attack-red -4.83399px -1.27771px 0px,
        @attack-red -4.48379px -2.2126px 0px,
        @attack-red -3.95484px -3.05929px 0px,
        @attack-red -3.26822px -3.78401px 0px,
        @attack-red -2.4513px -4.35788px 0px,
        @attack-red -1.53666px -4.75801px 0px,
        @attack-red -0.560763px -4.96845px 0px,
        @attack-red 0.437495px -4.98082px 0px,
        @attack-red 1.41831px -4.79462px 0px,
        @attack-red 2.34258px -4.41727px 0px,
        @attack-red 3.17346px -3.86382px 0px,
        @attack-red 3.87783px -3.15633px 0px,
        @attack-red 4.4276px -2.32301px 0px,
        @attack-red 4.80085px -1.39708px 0px,
        @attack-red 4.98271px -0.415447px 0px;

      background-image: linear-gradient(
        45deg,
        @attack-red 25%,
        @versatile-violet 25%,
        @versatile-violet 50%,
        @attack-red 50%,
        @attack-red 75%,
        @versatile-violet 75%,
        @versatile-violet 100%
      );
      background-size: 1em 1em;
    }
  }

  .defense,
  .defence {
    background-color: @defence-blue !important;
    color: white;

    &.include-versatile {
      text-shadow: @defence-blue 5px 0px 0px,
        @defence-blue 4.90033px 0.993347px 0px,
        @defence-blue 4.60531px 1.94709px 0px,
        @defence-blue 4.12668px 2.82321px 0px,
        @defence-blue 3.48353px 3.58678px 0px,
        @defence-blue 2.70151px 4.20736px 0px,
        @defence-blue 1.81179px 4.6602px 0px,
        @defence-blue 0.849836px 4.92725px 0px,
        @defence-blue -0.145998px 4.99787px 0px,
        @defence-blue -1.13601px 4.86924px 0px,
        @defence-blue -2.08073px 4.54649px 0px,
        @defence-blue -2.94251px 4.04248px 0px,
        @defence-blue -3.68697px 3.37732px 0px,
        @defence-blue -4.28444px 2.57751px 0px,
        @defence-blue -4.71111px 1.67494px 0px,
        @defence-blue -4.94996px 0.7056px 0px,
        @defence-blue -4.99147px -0.291871px 0px,
        @defence-blue -4.83399px -1.27771px 0px,
        @defence-blue -4.48379px -2.2126px 0px,
        @defence-blue -3.95484px -3.05929px 0px,
        @defence-blue -3.26822px -3.78401px 0px,
        @defence-blue -2.4513px -4.35788px 0px,
        @defence-blue -1.53666px -4.75801px 0px,
        @defence-blue -0.560763px -4.96845px 0px,
        @defence-blue 0.437495px -4.98082px 0px,
        @defence-blue 1.41831px -4.79462px 0px,
        @defence-blue 2.34258px -4.41727px 0px,
        @defence-blue 3.17346px -3.86382px 0px,
        @defence-blue 3.87783px -3.15633px 0px,
        @defence-blue 4.4276px -2.32301px 0px,
        @defence-blue 4.80085px -1.39708px 0px,
        @defence-blue 4.98271px -0.415447px 0px;
      background-image: linear-gradient(
        45deg,
        @versatile-violet 25%,
        @defence-blue 25%,
        @defence-blue 50%,
        @versatile-violet 50%,
        @versatile-violet 75%,
        @defence-blue 75%,
        @defence-blue 100%
      );
      background-size: 1em 1em;
    }
  }

  .versatile {
    background-color: @versatile-violet !important;
    color: white;
  }

  .scheme {
    background-color: @scheme-yellow !important;
    color: white;
  }

  td,
  th {
    white-space: nowrap;

    &.histogram {
      padding: 0;
    }
  }
}
</style>
